/**
 * Engagement types — Phase 6a.8b per Frontend Spec §21 / Backend Spec §20.5.
 *
 * Mirrors the backend `src/types/engagement.ts` shapes byte-for-byte. All
 * Decimal values are strings on the wire (backend uses Decimal.js with
 * toFixed(2)/toFixed(10) at the JSON boundary).
 *
 * Single-contract types are reused from `./contract` and `./result`.
 */

import type { ContractInput } from "./contract";
import type { CalculationResult, JournalEntryType } from "./result";

// ─── Inputs ─────────────────────────────────────────────────────────────────

export interface OpeningBalances {
  rouAssetCost: string;
  rouAccumulatedDepreciation: string;
  leaseLiability: string;
  leaseLiabilityShortTerm?: string;
  leaseLiabilityLongTerm?: string;
}

/**
 * Phase 6c.2 (Backend §20.3, Frontend Spec §21.3) — 3-mode opening
 * balance taxonomy. Legacy `"AUTO"` is rewritten to
 * `"IFRS_FIRST_ADOPTION"` by the backend preprocessor + emits a
 * deprecation warning; the frontend always sends one of the three
 * canonical values.
 */
export type OpeningBalanceMode =
  | "IFRS_FIRST_ADOPTION"
  | "AUTO_COMPUTED"
  | "MANUAL";

export const OPENING_BALANCE_MODES = [
  "IFRS_FIRST_ADOPTION",
  "AUTO_COMPUTED",
  "MANUAL",
] as const satisfies ReadonlyArray<OpeningBalanceMode>;

/**
 * Phase 6c.2 (Backend §20.3, Frontend Spec §21.7.1) — 6-value asset
 * class enum required on every contract. The frontend dropdown forces
 * an explicit choice via a placeholder "Seçiniz..." — there is NO
 * default at any layer (Zod rejects missing).
 */
export type AssetClass =
  | "BUILDINGS"
  | "VEHICLES"
  | "MACHINERY"
  | "OFFICE_EQUIPMENT"
  | "IT_EQUIPMENT"
  | "OTHER";

export const ASSET_CLASS_ORDER = [
  "BUILDINGS",
  "VEHICLES",
  "MACHINERY",
  "OFFICE_EQUIPMENT",
  "IT_EQUIPMENT",
  "OTHER",
] as const satisfies ReadonlyArray<AssetClass>;

export interface EngagementInput {
  clientName: string;
  auditPeriodStart: string; // ISO date YYYY-MM-DD
  auditPeriodEnd: string; // ISO date YYYY-MM-DD
  functionalCurrency: string; // ISO 4217 (3 letters)
  openingBalanceMode: OpeningBalanceMode;
  /** Required when openingBalanceMode === "MANUAL". */
  openingBalances?: OpeningBalances;
  /** currency → closing-rate string. Required for non-functional contracts. */
  closingFxRates?: Record<string, string>;
}

/**
 * Phase 6c.2 — engagement-layer contracts must declare an `assetClass`.
 * The single-contract `POST /api/calculate` endpoint stays on the
 * unextended `ContractInput`.
 */
export type EngagementContractInput = ContractInput & {
  assetClass: AssetClass;
};

export interface CalculateEngagementRequest {
  engagement: EngagementInput;
  contracts: EngagementContractInput[];
}

// ─── Aggregations ───────────────────────────────────────────────────────────

export type MovementRowType =
  | "ADDITIONS"
  | "TERMINATIONS"
  | "MODIFICATIONS"
  | "INTEREST_ACCRUAL"
  | "PAYMENTS"
  | "DEPRECIATION"
  | "DEPRECIATION_REVERSAL"
  | "INDEX_REMEASUREMENT"
  | "FX_REMEASUREMENT"
  | "FX_REVALUATION"
  | "RECLASSIFICATION";

export interface MovementRow {
  type: MovementRowType;
  amount: string;
  description: string;
}

export interface PerContractMovementContribution {
  contractId: string;
  contractIndex: number;
  contributions: Partial<Record<MovementRowType | "openingBalance", string>>;
}

export interface MovementTable {
  openingBalance: string;
  rows: MovementRow[];
  closingBalance: string;
  perContractContributions: PerContractMovementContribution[];
  /**
   * Phase 6c.2 (Backend §20.6.3.3) — populated only in `MANUAL` mode
   * when the user-supplied opening differs from the tool-computed
   * opening by more than 0.01 functional units. Signed Decimal string
   * (user − tool). Frontend renders this in the Movement Tab footer
   * ONLY — Balance Sheet shows the user-supplied opening verbatim.
   */
  reconciliationVariance?: string;
}

/**
 * Phase 6c.2 (Backend §20.5, Frontend Spec §21.8.2) — per-class
 * breakdown for ROU Cost + ROU AccDep. `byAssetClass` contains an
 * entry ONLY for classes present in the engagement; the renderer
 * iterates `ASSET_CLASS_ORDER` and skips absent keys. `total` is
 * derived from `byAssetClass[*]` byte-exactly.
 */
export interface AssetClassMovementTable {
  byAssetClass: Partial<Record<AssetClass, MovementTable>>;
  total: MovementTable;
}

/**
 * Phase 6c.2 (Backend §20.6.10, Frontend Spec §21.8.2a) — IFRS 16
 * §B53 lessee maturity disclosure. `ranges` is always exactly 6
 * buckets (every bucket present, zero rendered as `"0.00"` for UI
 * stability). `discountedTotal` matches
 * `balanceSheet.leaseLiability.closing` byte-exactly.
 */
export type MaturityBucket =
  | "WITHIN_1_YEAR"
  | "BETWEEN_1_AND_2_YEARS"
  | "BETWEEN_2_AND_3_YEARS"
  | "BETWEEN_3_AND_4_YEARS"
  | "BETWEEN_4_AND_5_YEARS"
  | "BEYOND_5_YEARS";

export const MATURITY_BUCKET_ORDER = [
  "WITHIN_1_YEAR",
  "BETWEEN_1_AND_2_YEARS",
  "BETWEEN_2_AND_3_YEARS",
  "BETWEEN_3_AND_4_YEARS",
  "BETWEEN_4_AND_5_YEARS",
  "BEYOND_5_YEARS",
] as const satisfies ReadonlyArray<MaturityBucket>;

export interface MaturityRange {
  bucket: MaturityBucket;
  amount: string;
}

export interface MaturityAnalysis {
  ranges: MaturityRange[];
  total: string;
  discountedTotal: string;
  futureInterest: string;
}

export interface BalanceSheetPresentation {
  rouAsset: {
    cost: { opening: string; closing: string };
    accumulatedDepreciation: { opening: string; closing: string };
    netBookValue: { opening: string; closing: string };
  };
  leaseLiability: {
    opening: string;
    closing: string;
    closingShortTerm: string;
    closingLongTerm: string;
  };
  npv: {
    rouAssetNet: string;
    leaseLiabilityTotal: string;
    netPosition: string;
  };
}

export interface PerContractPLBreakdown {
  contractId: string;
  contractIndex: number;
  interest: string;
  depreciation: string;
  variableExpense: string;
  modGainLoss: string;
  terminationGainLoss: string;
  fxGainLoss: string;
  net: string;
}

export interface PLImpactSummary {
  interestExpense: string;
  depreciationExpense: string;
  variableLeaseExpense: string;
  modificationGainLoss: string;
  terminationGainLoss: string;
  fxGainLoss: string;
  netPLEffect: string;
  perContractBreakdown: PerContractPLBreakdown[];
}

export interface AggregatedJELine {
  account: string;
  debit: string;
  credit: string;
  /**
   * Phase 6c.2 (Backend §20.5) — present only when `account` is a
   * per-class ROU asset account (e.g. `"Right-of-Use Asset — Buildings"`).
   */
  assetClass?: AssetClass;
}

export interface AggregatedJournalEntry {
  period: string;
  type: JournalEntryType;
  lines: AggregatedJELine[];
  contractIds: string[];
  contractCount: number;
}

export type AggregatedPajeType =
  | "RECLASSIFICATION"
  | "ADJUSTMENT"
  | "FX_REVALUATION";

export interface AggregatedPaje {
  pajeNumber: number;
  contractId: string;
  contractIndex: number;
  description: string;
  variance: string;
  type: AggregatedPajeType;
  suggestedJE: AggregatedJELine[];
}

export interface ContractSummary {
  contractIndex: number;
  contractId: string;
  description?: string;
  commencementDate: string;
  termMonths: number;
  currency: string;
  /**
   * Phase 6c.2 (Backend §20.5) — echoed from the engagement contract
   * input. The frontend uses this for per-row colour/label coding in
   * the Contracts tab and the per-contract drill-down.
   */
  assetClass: AssetClass;
  rouAssetClosing: string;
  leaseLiabilityClosing: string;
  hasModifications: boolean;
  hasTermination: boolean;
  hasIndexLinkage: boolean;
  isMultiCurrency: boolean;
  hasPriorReconciliation: boolean;
}

export interface EngagementAggregations {
  balanceSheet: BalanceSheetPresentation;
  movement: {
    /** Phase 6c.2 — per-class breakdown + TOTAL row. */
    rouAssetCost: AssetClassMovementTable;
    rouAccumulatedDepreciation: AssetClassMovementTable;
    /** Phase 6c.2 — UNCHANGED: lease liability stays class-agnostic. */
    leaseLiability: MovementTable;
  };
  /** Phase 6c.2 (Backend §20.6.10) — IFRS §B53 maturity disclosure. */
  maturityAnalysis: MaturityAnalysis;
  plImpact: PLImpactSummary;
  aggregatedJournalEntries: AggregatedJournalEntry[];
  pajes: AggregatedPaje[];
  contractSummaries: ContractSummary[];
}

/**
 * Phase 6c.2 (Backend §20.6.8, Frontend Spec §21.3.2 / §21.6.5) —
 * non-blocking advisory the backend emits alongside a successful
 * calculation. The `messageTr` field is spec-authoritative
 * (auditor-approved Turkish) and rendered VERBATIM by the
 * WarningsBanner.
 */
export interface WarningItem {
  code:
    | "FIRST_ADOPTION_WITH_PRE_PERIOD_CONTRACT"
    | "AUTO_COMPUTED_RECONCILIATION_AVAILABLE"
    | "MANUAL_OPENING_VARIANCE"
    | "LEGACY_AUTO_MODE_DEPRECATED";
  message: string; // EN
  messageTr: string; // TR — rendered verbatim by the banner
  affectedContractIds: string[];
}

// ─── Response Envelopes ─────────────────────────────────────────────────────

export interface CalculateEngagementSuccessResponse {
  success: true;
  data: {
    engagementId: string;
    perContract: CalculationResult[];
    aggregations: EngagementAggregations;
    /**
     * Phase 6c.2 (Backend §20.6.8) — optional advisory list. Absent or
     * empty when no warnings raised; otherwise consumed by the
     * WarningsBanner above the result tab strip.
     */
    warnings?: WarningItem[];
    /**
     * Phase 6c.2 (Backend §20.7.1) — canonical mode echoed back to the
     * client. For legacy `"AUTO"` requests this is
     * `"IFRS_FIRST_ADOPTION"` (the post-migration name).
     */
    openingBalanceMode: OpeningBalanceMode;
  };
}

export interface CalculateEngagementErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    messageTr: string;
    details?: {
      engagementErrors?: Array<{ field: string; code: string }>;
      failedContracts?: Array<{
        contractIndex: number;
        contractId: string;
        errors: Array<{ field: string; code: string }>;
      }>;
    };
  };
}

export type CalculateEngagementResponse =
  | CalculateEngagementSuccessResponse
  | CalculateEngagementErrorResponse;

// ─── localStorage entry shape (Spec §21.11) ─────────────────────────────────

/**
 * Engagement lifecycle state machine (Phase 6a.10 / Spec §21.7.4).
 *
 *   draft       → never calculated (initial state after creation)
 *   calculated  → backend POST /api/engagements/calculate succeeded
 *   stale       → was calculated, then a contract slot was edited
 *
 * Transitions:
 *   draft       → calculated (on successful calculation)
 *   calculated  → stale       (on any contract dirty after calculation)
 *   stale       → calculated  (on next successful calculation)
 */
export type EngagementStatus = "draft" | "calculated" | "stale";

export interface EngagementHistoryEntry {
  id: string;
  clientName: string;
  auditPeriodStart: string;
  auditPeriodEnd: string;
  functionalCurrency: string;
  /**
   * Phase 6c.2 (Frontend Spec §21.6.3) — captured at engagement
   * creation and LOCKED for the rest of the engagement's lifetime. The
   * radio group renders disabled on subsequent visits (the future
   * Phase 6a.19 edit flow will introduce a controlled unlock).
   */
  openingBalanceMode: OpeningBalanceMode;
  /**
   * Phase 6c.2 — populated only when `openingBalanceMode === "MANUAL"`.
   * Persisted alongside the mode so subsequent recalculations send the
   * same opening basis (auditor doesn't re-enter on every recalculate).
   */
  openingBalances?: OpeningBalances;
  /**
   * Phase 6a.23 — engagement-level closing FX rates, keyed by currency
   * code. Required at calculate-time whenever ANY contract has
   * `currency !== functionalCurrency` (Backend Spec §20.3 line 2230 —
   * `MISSING_CLOSING_FX_RATE` rejection otherwise). Stored as 2-decimal
   * Decimal-style strings (matches `openingBalances.*` precedent) so
   * trailing zeros and audit-grade precision round-trip correctly.
   */
  closingFxRates?: Record<string, string>;
  /**
   * Phase 6e.3 — last TCMB FX sync audit summary. Populated by the
   * Magic Button (`applyFxSyncPlan`) so the audit accordion can
   * render across re-renders without re-fetching from TCMB. Kept as
   * an opaque `unknown`-typed slot at the persistence layer to avoid
   * dragging `FxSyncAudit` into this type module; the Magic Button
   * casts at the boundary.
   */
  lastFxSync?: import("@/features/fx-sync/types").FxSyncAudit | null;
  contractCount: number;
  /**
   * Append-only slot array (Phase 6a.10 / Spec §21.7.8). A `null` entry
   * is an empty slot the user has not filled yet. Older entries may
   * have `ContractInput[]` shape; both are tolerated at read time.
   */
  contracts: Array<ContractInput | null>;
  /**
   * Per-slot dirty flag (Phase 6a.10 / Spec §21.7.3). Same length as
   * `contracts`. `true` once the user has touched the slot via the
   * contract entry form. Drives the `partial` vs `empty` distinction
   * for the status dot row.
   */
  contractsDirty?: boolean[];
  lastResult?: {
    engagementId: string;
    perContract: CalculationResult[];
    aggregations: EngagementAggregations;
    calculatedAt: string;
    /**
     * Phase 6c.2 (Backend §20.6.8) — non-blocking advisories surfaced
     * by the calculation. Persisted so the WarningsBanner re-renders
     * on every result page open without requiring a recalculation.
     */
    warnings?: WarningItem[];
  };
  /**
   * Engagement lifecycle status (Phase 6a.10 / Spec §21.7.4). Defaults
   * to "draft" for newly created engagements; promoted by
   * `transitionToCalculated` / `transitionToStale` helpers.
   */
  status?: EngagementStatus;
  /** ISO timestamp set when the engagement first enters "calculated". */
  calculatedAt?: string;
  /** ISO timestamp set when the engagement transitions to "stale". */
  staleSince?: string;
  /**
   * Backend rejection state from the most recent failed Calculate attempt
   * (Phase 6a.11 / Spec §21.7.3 v1.8). Keys are 0-based contract indices
   * matching the `contracts` array; values are arrays of Zod error
   * messages returned by the backend. Cleared automatically (per-slot)
   * when the user edits a field of a rejected contract — the auto-clear
   * lives in `useEngagementContractDraft`'s `persist`.
   */
  lastCalculateError?: Record<number, string[]>;
  isStale: boolean;
  isDeleted: boolean;
  deletedAt?: string;
  createdAt: string;
  updatedAt: string;
}
